import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Plus } from 'lucide-react';
import { usePlans } from './hooks/usePlans';
import { PlansTable, PlanDialog } from './components';
import { PlansPageProps } from './types';

export default function PlansPage({ isDark }: PlansPageProps) {
  const {
    plans,
    isLoading,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    setIncludeInactive,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  } = usePlans();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uz-UZ', {
      style: 'currency',
      currency: 'UZS',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDuration = (value: number, unit: string) => {
    const unitMap: Record<string, string> = {
      week: 'нед',
      month: 'мес',
      year: 'год',
    };
    return `${value} ${unitMap[unit] || unit}`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#0F172A]'
          )}>Plans</h1>
          <p className={cn(
            'text-sm mt-1',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Manage subscription plans</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} />
            <Label className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
              Show inactive
            </Label>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Plan
          </Button>
        </div>
      </div>

      {/* Plans Table */}
      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark
          ? 'bg-[#1E293B] shadow-lg shadow-black/10'
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        <PlansTable
          isDark={isDark}
          plans={plans}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
          formatPrice={formatPrice}
          formatDuration={formatDuration}
        />
      </Card>

      {/* Create Dialog */}
      <PlanDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        title="Create Plan"
        description="Add a new subscription plan"
        submitLabel="Create"
      />

      {/* Edit Dialog */}
      <PlanDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        title="Edit Plan"
        description="Update plan information"
        submitLabel="Save Changes"
      />
    </div>
  );
}
